import { core, safeToNumber, toJSON, toJSONArray } from '@tock/coretypes';
import { dashboard } from 'types';
import _ from 'lodash';
import update from 'react-addons-update';

import { ReorderSurveyData, ReorderTicketsData } from 'actions/TicketsActions';
import { fastParseTime } from 'utils/timeUtils';
import AppConstants from 'constants/AppConstants';
import AppSettings from 'settings/AppSettings';

interface State {
  list: {
    ticketTypes: core.TicketType[];
    isInitialized: boolean;
    isRequestInProgress: boolean;
    eventTemplates: {
      [key: number]: core.EventTemplate[];
    };
  };
  single: {
    ticketType?: core.TicketType;
    menu?: dashboard.Menu;
    isInitialized: boolean;
    isRequestInProgress: boolean;
    promoCodes: core.TicketTypePromoCode[];
    isPromoCodesRequestInProgress: boolean;
    eventTemplates: core.EventTemplate[];
    isEventTemplatesRequestInProgress: boolean;
    isEventTemplatesInitialized: boolean;
    isDefaultQuestionsRequestInProgress: boolean;
    isDefaultQuestionsInitialized: boolean;
    defaultQuestions: core.TicketTypeQuestion[];
  };
}

const initialState: State = {
  list: {
    ticketTypes: [],
    isInitialized: false,
    isRequestInProgress: false,
    eventTemplates: {},
  },
  single: {
    ticketType: undefined,
    menu: undefined,
    isInitialized: false,
    isRequestInProgress: false,
    promoCodes: [],
    isPromoCodesRequestInProgress: false,
    eventTemplates: [],
    isEventTemplatesRequestInProgress: false,
    isEventTemplatesInitialized: false,
    isDefaultQuestionsRequestInProgress: false,
    isDefaultQuestionsInitialized: false,
    defaultQuestions: [],
  },
};

function sortEventTemplateSlotsByTime(eventTemplate: core.EventTemplate) {
  return {
    ...eventTemplate,
    eventTemplateSlotConfig: _.sortBy(eventTemplate.eventTemplateSlotConfig, (slotConfig) => {
      return slotConfig.startTime
        ? fastParseTime(slotConfig.startTime).minusHours(4).toString()
        : AppSettings.LAST_TIME_IN_BUSINESS_DAY;
    }),
  };
}

export default function reducer(state = initialState, action: BaseTockAction): State {
  const { type, payload, context } = action;

  switch (type) {
    /**
     * API STARTING ACTIONS
     */
    case AppConstants.TICKETS_FETCH_SINGLE_STARTING: {
      return {
        ...state,
        single: {
          ...state.single,
          ticketType: context.clearCurrentValue
            ? initialState.single.ticketType
            : state.single.ticketType,
          isRequestInProgress: true,
        },
      };
    }
    case AppConstants.TICKET_EVENT_TEMPLATES_GET_STARTING:
      return {
        ...state,
        single: {
          ...state.single,
          isEventTemplatesRequestInProgress: true,
          isEventTemplatesInitialized: context?.resetInitialization
            ? false
            : state.single.isEventTemplatesInitialized,
        },
      };
    case AppConstants.TICKET_PROMOTION_FETCH_ALL_STARTING:
    case AppConstants.TICKET_PROMOTION_INSERT_STARTING:
    case AppConstants.TICKET_PROMOTION_UPDATE_STARTING:
      return {
        ...state,
        single: {
          ...state.single,
          isPromoCodesRequestInProgress: true,
        },
      };

    case AppConstants.TICKETS_UPDATE_STARTING:
    case AppConstants.TICKETS_UPDATE_EVENT_DATE_STARTING:
    case AppConstants.TICKETS_SURVEY_UPDATE_STARTING:
    case AppConstants.TICKET_MENU_GET_STARTING:
    case AppConstants.TICKET_MENU_REMOVE_STARTING:
    case AppConstants.TICKET_MENU_UPDATE_STARTING: {
      return {
        ...state,
        single: {
          ...state.single,
          isRequestInProgress: true,
          isInitialized: false,
        },
      };
    }
    case AppConstants.TICKETS_FETCH_ALL_STARTING: {
      return {
        ...state,
        list: {
          ...state.list,
          ticketTypes: context?.isAdditive ? state.list.ticketTypes : initialState.list.ticketTypes,
          isRequestInProgress: true,
        },
      };
    }
    case AppConstants.TICKETS_ADD_STARTING:
    case AppConstants.TICKETS_UPDATE_ALL_STARTING: {
      return {
        ...state,
        list: {
          ...state.list,
          isRequestInProgress: true,
        },
      };
    }

    /**
     * API FAILED ACTIONS
     */
    case AppConstants.TICKETS_FETCH_ALL_FAILED: {
      return {
        ...state,
        list: {
          ...state.list,
          isRequestInProgress: false,
          isInitialized: true,
        },
      };
    }
    case AppConstants.TICKETS_ADD_FAILED:
    case AppConstants.TICKETS_UPDATE_ALL_FAILED: {
      return {
        ...state,
        list: {
          ...state.list,
          isRequestInProgress: false,
        },
      };
    }
    case AppConstants.TICKET_EVENT_TEMPLATES_GET_FAILED:
      return {
        ...state,
        single: {
          ...state.single,
          isEventTemplatesRequestInProgress: false,
        },
      };
    case AppConstants.TICKET_PROMOTION_FETCH_ALL_FAILED:
    case AppConstants.TICKET_PROMOTION_INSERT_FAILED:
    case AppConstants.TICKET_PROMOTION_UPDATE_FAILED:
      return {
        ...state,
        single: {
          ...state.single,
          isPromoCodesRequestInProgress: true,
        },
      };
    case AppConstants.TICKETS_FETCH_SINGLE_FAILED:
      return {
        ...state,
        single: {
          ...state.single,
          isRequestInProgress: false,
          isInitialized: true,
        },
      };
    case AppConstants.TICKETS_UPDATE_FAILED:
    case AppConstants.TICKETS_UPDATE_EVENT_DATE_FAILED:
    case AppConstants.TICKETS_SURVEY_UPDATE_FAILED: {
      return {
        ...state,
        single: {
          ...state.single,
          isRequestInProgress: false,
          isInitialized: true,
        },
      };
    }
    case AppConstants.TICKET_MENU_GET_FAILED: {
      return {
        ...state,
        single: {
          ...state.single,
          menu: undefined,
          isRequestInProgress: false,
          isInitialized: true,
        },
      };
    }

    /**
     * API DONE ACTIONS
     */
    case AppConstants.TICKETS_FETCH_SINGLE_DONE: {
      const data = toJSON<core.TicketType>(payload);
      return {
        ...state,
        single: {
          ...state.single,
          ticketType: data,
          isRequestInProgress: false,
          isInitialized: true,
        },
      };
    }
    case AppConstants.TICKETS_FETCH_ALL_DONE: {
      const data = toJSONArray<core.TicketType>(payload);
      return {
        ...state,
        list: {
          ...state.list,
          ticketTypes: context?.isAdditive ? [...state.list.ticketTypes, ...data] : data,
          isInitialized: true,
          isRequestInProgress: false,
        },
      };
    }
    case AppConstants.TICKETS_ADD_DONE: {
      return {
        ...state,
        single: {
          ...state.single,
          isRequestInProgress: false,
          isInitialized: true,
        },
      };
    }
    case AppConstants.TICKETS_UPDATE_EVENT_DATE_DONE:
    case AppConstants.TICKETS_UPDATE_DONE: {
      const ticketType = toJSON<core.TicketType>(payload);
      const idx = _.findIndex(
        state.list.ticketTypes,
        (existingTicket) => existingTicket.id === ticketType.id
      );
      const ticketTypes = [
        ...state.list.ticketTypes.slice(0, idx),
        ticketType,
        ...state.list.ticketTypes.slice(idx + 1),
      ];

      return {
        ...state,
        list: {
          ...state.list,
          ticketTypes,
        },
        single: {
          ...state.single,
          ticketType,
          isRequestInProgress: false,
          isInitialized: true,
        },
      };
    }
    case AppConstants.TICKETS_EVENT_TEMPLATES_GET_DONE: {
      const eventTemplates = toJSONArray<core.EventTemplate>(payload);
      return {
        ...state,
        list: {
          ...state.list,
          eventTemplates: _.groupBy(eventTemplates, 'ticketTypeId'),
        },
        single: {
          ...state.single,
          eventTemplates,
        },
      };
    }
    case AppConstants.TICKET_EVENT_TEMPLATES_GET_DONE: {
      return {
        ...state,
        single: {
          ...state.single,
          eventTemplates: toJSONArray<core.EventTemplate>(payload).map(
            sortEventTemplateSlotsByTime
          ),
          isEventTemplatesRequestInProgress: false,
          isEventTemplatesInitialized: true,
        },
      };
    }
    case AppConstants.TICKET_EVENT_TEMPLATE_INSERT_DONE: {
      return {
        ...state,
        single: {
          ...state.single,
          eventTemplates: [...state.single.eventTemplates, toJSON<core.EventTemplate>(payload)],
          isEventTemplatesRequestInProgress: false,
        },
      };
    }
    case AppConstants.TICKET_EVENT_TEMPLATE_UPDATE_DONE: {
      return {
        ...state,
        single: {
          ...state.single,
          isEventTemplatesRequestInProgress: false,
        },
      };
    }
    case AppConstants.TICKET_PROMOTION_FETCH_ALL_DONE: {
      return {
        ...state,
        single: {
          ...state.single,
          promoCodes: _.orderBy(toJSONArray<core.TicketTypePromoCode>(payload), ['name']),
          isPromoCodesRequestInProgress: false,
        },
      };
    }
    case AppConstants.TICKETS_UPDATE_ALL_DONE: {
      const changedTickets = toJSONArray<core.TicketType>(payload);
      const unchangedTickets = _.xorBy(state.list.ticketTypes, changedTickets, (ticket) => {
        return ticket.id;
      });

      const ticketTypes = _.concat(unchangedTickets, changedTickets);

      return {
        ...state,
        list: {
          ...state.list,
          ticketTypes,
          isRequestInProgress: false,
        },
      };
    }
    case AppConstants.TICKETS_SURVEY_UPDATE_DONE: {
      return {
        ...state,
        single: {
          ...state.single,
          isRequestInProgress: false,
          ticketType: state.single.ticketType && {
            ...state.single.ticketType,
            question: toJSONArray<core.TicketTypeQuestion>(payload),
          },
        },
      };
    }
    case AppConstants.TICKET_DEFAULT_QUESTION_FETCH_ALL_STARTING: {
      return {
        ...state,
        single: {
          ...state.single,
          isDefaultQuestionsRequestInProgress: true,
        },
      };
    }
    case AppConstants.TICKET_DEFAULT_QUESTION_FETCH_ALL_FAILED: {
      return {
        ...state,
        single: {
          ...state.single,
          isDefaultQuestionsRequestInProgress: false,
          isDefaultQuestionsInitialized: true,
        },
      };
    }
    case AppConstants.TICKET_DEFAULT_QUESTION_FETCH_ALL_DONE: {
      return {
        ...state,
        single: {
          ...state.single,
          isDefaultQuestionsRequestInProgress: false,
          isDefaultQuestionsInitialized: true,
          defaultQuestions: toJSONArray<core.TicketTypeQuestion>(payload),
        },
      };
    }
    case AppConstants.TICKETS_SELECT_FROM_LIST: {
      return {
        ...state,
        single: {
          ...state.single,
          ticketType: payload,
        },
      };
    }
    case AppConstants.TICKET_MENU_GET_DONE:
    case AppConstants.TICKET_MENU_UPDATE_DONE: {
      return {
        ...state,
        single: {
          ...state.single,
          menu: toJSON<dashboard.Menu>(payload),
          isRequestInProgress: false,
          isInitialized: true,
        },
      };
    }
    case AppConstants.TICKET_MENU_REMOVE_DONE: {
      return {
        ...state,
        single: {
          ...state.single,
          menu: undefined,
          isRequestInProgress: false,
        },
      };
    }

    /**
     * NON-API ACTIONS
     */
    case AppConstants.TICKETS_REORDER: {
      const { tickets, draggedItemIndex, hoveredItemIndex }: ReorderTicketsData = payload;
      const draggedTicket = tickets[draggedItemIndex];

      const newTickets = update(tickets, {
        $splice: [
          [draggedItemIndex, 1],
          [hoveredItemIndex, 0, draggedTicket],
        ],
      });

      const orderingMap = _.map(newTickets, (ticket, index) => ({
        id: ticket.id,
        ordering: index,
      }));

      const data = _.map(state.list.ticketTypes, (ticket) => {
        const newOrdering = _.find(orderingMap, (orderedTicket) => orderedTicket.id === ticket.id);

        if (newOrdering) {
          ticket.ordering = newOrdering.ordering;
        }

        return ticket;
      });

      return {
        ...state,
        list: {
          ...state.list,
          ticketTypes: data,
        },
      };
    }
    case AppConstants.TICKETS_SURVEY_REORDER: {
      const { questions, draggedItemIndex, hoveredItemIndex }: ReorderSurveyData = payload;

      const draggedQuestion = questions[draggedItemIndex];
      const orderedQuestions: Array<core.ITicketTypeQuestion> = update(questions, {
        $splice: [
          [draggedItemIndex, 1],
          [hoveredItemIndex, 0, draggedQuestion],
        ],
      });

      const newQuestions: core.ITicketTypeQuestion[] = _.map(
        orderedQuestions,
        (question: core.ITicketTypeQuestion, index: number) => {
          return {
            ...question,
            ordering: index,
          };
        }
      );

      const updatedTicketType = state.single.ticketType
        ? {
            ...state.single.ticketType,
            question: newQuestions,
          }
        : undefined;

      return {
        ...state,
        single: {
          ...state.single,
          ticketType: updatedTicketType,
        },
      };
    }
    case AppConstants.SUPPLEMENTS_REORDER: {
      const { draggedItemIndex, hoveredItemIndex, supplements, supplementGroup } = payload;

      if (!state.single.ticketType) {
        return { ...state };
      }

      const sortSupplements = (supplementA: core.ITicketOption, supplementB: core.ITicketOption) =>
        (supplementA.ordering ?? Infinity) - (supplementB.ordering ?? Infinity);

      const sortedSupplements = supplements.sort(sortSupplements);

      const selectedSupplementGroup = _.find(state.single.ticketType.optionGroup, (optionGroup) => {
        return optionGroup.id === safeToNumber(supplementGroup.id);
      });

      if (!selectedSupplementGroup) {
        return { ...state };
      }

      const selectedSupplementGroupIndex = _.findIndex(
        state.single.ticketType.optionGroup,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (optionGroup: any) => {
          return safeToNumber(optionGroup.id) === safeToNumber(supplementGroup.id);
        }
      );

      const draggedSupplement = sortedSupplements[draggedItemIndex];
      const orderedSupplements = update(sortedSupplements, {
        $splice: [
          [draggedItemIndex, 1],
          [hoveredItemIndex, 0, draggedSupplement],
        ],
      });

      const newSupplements = _.map(orderedSupplements, (supplement, index) => {
        supplement.ordering = index;
        return supplement;
      });

      const updatedSupplementGroup = {
        ...selectedSupplementGroup,
        ticketOption: newSupplements,
      };
      selectedSupplementGroup.ticketOption = newSupplements;

      const updatedTicketType = {
        ...state.single.ticketType,
        ticketOption: [
          ...state.single.ticketType.optionGroup.slice(0, selectedSupplementGroupIndex),
          updatedSupplementGroup,
          ...state.single.ticketType.optionGroup.slice(selectedSupplementGroupIndex + 1),
        ],
      };

      return {
        ...state,
        single: {
          ...state.single,
          ticketType: updatedTicketType,
        },
      };
    }
    case AppConstants.SUPPLEMENT_GROUPS_REORDER: {
      const { draggedItemIndex, hoveredItemIndex } = payload;

      if (!state.single.ticketType) {
        return { ...state };
      }

      const sortSupplementGroups = (
        groupA: core.ITicketOptionGroup,
        groupB: core.ITicketOptionGroup
      ) => (groupA.ordering ?? Infinity) - (groupB.ordering ?? Infinity);

      const supplementGroups: core.ITicketOptionGroup[] = payload.supplementGroups
        .filter((g: core.ITicketOptionGroup) => !g.parentTicketOptionId)
        .sort(sortSupplementGroups);
      const modifiers: core.ITicketOptionGroup[] = payload.supplementGroups
        .filter((g: core.ITicketOptionGroup) => g.parentTicketOptionId)
        .sort(sortSupplementGroups);

      const draggedSupplementGroup = supplementGroups[draggedItemIndex];
      const orderedSupplementGroups = update(supplementGroups, {
        $splice: [
          [draggedItemIndex, 1],
          [hoveredItemIndex, 0, draggedSupplementGroup],
        ],
      });

      const newSupplementGroups = _.map(orderedSupplementGroups, (supplementGroup, index) => {
        supplementGroup.ordering = index;
        return supplementGroup;
      });

      const updatedTicketType = {
        ...state.single.ticketType,
        optionGroup: [...newSupplementGroups, ...modifiers],
      };

      return {
        ...state,
        single: {
          ...state.single,
          ticketType: updatedTicketType,
        },
      };
    }
    case AppConstants.SUPPLEMENT_MODIFIERS_REORDER: {
      const { modifiers, draggedItemIndex, hoveredItemIndex } = payload;
      const draggedModifier = modifiers[draggedItemIndex];
      const orderedModifiers: core.ITicketOptionGroup[] = update(modifiers, {
        $splice: [
          [draggedItemIndex, 1],
          [hoveredItemIndex, 0, draggedModifier],
        ],
      });
      const newModifiers = _.map(orderedModifiers, (modifier, index) => {
        modifier.ordering = index;
        return modifier;
      });
      return {
        ...state,
        single: {
          ...state.single,
          ticketType: state.single.ticketType
            ? {
                ...state.single.ticketType,
                optionGroup: [
                  ...(state.single.ticketType.optionGroup ?? []).filter(
                    (optionGroup) => !newModifiers.includes(optionGroup)
                  ),
                  ...newModifiers,
                ],
              }
            : undefined,
        },
      };
    }
    case AppConstants.CLEAR_TICKET_MENU: {
      return {
        ...state,
        single: {
          ...state.single,
          menu: undefined,
        },
      };
    }

    default:
      return state;
  }
}
